import { useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import EditIcon from '@mui/icons-material/Edit';

import { getFragmentData, graphql } from '@e-socna/data-layer';
import type { FragmentType } from '@e-socna/data-layer';

import { SpeedDial } from '../../components/SpeedDial';
import { RightDrawer } from '../../components/RightDrawer';
import { FormVariant } from '../../forms';
import { ThirdPartyFormTab } from '../../forms/ThirdParty/enums/tab.enum';
import { PersonForm } from '../../forms/ThirdParty/Person';
import { BankInfo as BankInfoCmp } from '../../components/BankInfo';

export const PersonPage_BankInfo_Person_Fragment = graphql(`
  fragment PersonPage_BankInfo_Person_Fragment on Person {
    _id
    isInternal
    bankInfo {
      accountOwner
      domiciliation
      bankCode
      branchCode
      accountNumber
      certificateKey
      iban
      swift
      ...BankInfoCmp_BankInfo_Fragment
    }
    ...ThirdPartyForm_Person_Fragment
  }
`);

interface Props {
  readonly paths: Readonly<{
    consult: string;
    edit: string;
  }>;
  readonly person: FragmentType<typeof PersonPage_BankInfo_Person_Fragment>;
}

export const BankInfo = ({ paths, person: pPerson }: Readonly<Props>) => {
  const person = getFragmentData(PersonPage_BankInfo_Person_Fragment, pPerson);

  const { pathname: currentPath } = useLocation();
  const navigate = useNavigate();

  const isInEditionMode = currentPath === paths.edit;
  const leaveEditionMode = useCallback(
    () => void navigate(paths.consult),
    [navigate, paths.consult],
  );

  return (
    <>
      <BankInfoCmp bankInfo={person.bankInfo} />
      <SpeedDial
        actions={[
          {
            id: 'editPersonBankInfo',
            icon: <EditIcon />,
            name: 'Modifier Infos Bancaires',
            to: paths.edit,
          },
        ]}
        title="Actions possibles sur les Informations Bancaires de la Personne"
      />
      <RightDrawer isOpen={isInEditionMode} onDrawerClose={leaveEditionMode}>
        <PersonForm
          formVariant={FormVariant.Modification}
          initialValues={person}
          isInternal={person.isInternal}
          onCancel={leaveEditionMode}
          onSuccess={leaveEditionMode}
          personId={person._id}
          stepIdsSubset={[ThirdPartyFormTab.BankInfo]}
          title="Formulaire de modification des Informations Bancaires de la Personne"
        />
      </RightDrawer>
    </>
  );
};
